import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtMoney, fmtDate, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, SectionCard, StatCard, StatusBadge } from "@/components/Bits";
import { UploadButton } from "@/components/Upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Receipt, PlusCircle, Check, X, Paperclip, Trash2, Wallet, Clock, CircleDollarSign } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { category: "Medical", amount: "", description: "", receipt_file_id: null, claim_date: "" };

export default function Claims() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [claims, setClaims] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [reviewFor, setReviewFor] = useState(null);
  const [note, setNote] = useState("");
  const [payout, setPayout] = useState("payroll");
  const [flexi, setFlexi] = useState({ eligible: false, remaining: 0, allowance: 0, used: 0 });

  const load = useCallback(() => api.get("/claims").then((r) => setClaims(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/claims/meta").then((r) => setCats(r.data.categories)).catch(() => {});
    api.get("/claims/flexi-balance").then((r) => setFlexi(r.data)).catch(() => {});
  }, [load]);

  const catOptions = (cats || []).filter((c) => c !== "Flexi Care" || flexi.eligible);
  const isFlexi = form.category === "Flexi Care";
  const amt = parseFloat(form.amount || 0);
  const overMonthly = isFlexi && flexi.monthly_remaining != null && amt > flexi.monthly_remaining;
  const overFlexi = isFlexi && (amt > flexi.remaining || overMonthly);

  const submit = async () => {
    try {
      await api.post("/claims", { ...form, amount: parseFloat(form.amount || 0) });
      toast.success("Claim submitted for review"); setOpen(false); setForm(EMPTY); load();
      api.get("/claims/flexi-balance").then((r) => setFlexi(r.data)).catch(() => {});
    } catch (e) { toast.error(apiErr(e)); }
  };

  const review = async (id, action) => {
    try {
      await api.put(`/claims/${id}/review`, { action, review_note: note, payout_method: payout });
      toast.success(`Claim ${action === "approve" ? "approved" : "rejected"}`); setReviewFor(null); setNote(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const payCash = async (id, f) => {
    try { await api.post(`/claims/${id}/pay-cash`, { voucher_file_id: f.id }); toast.success("Cash voucher uploaded — claim marked Paid"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this claim?")) return;
    try { await api.delete(`/claims/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const pending = claims.filter((c) => c.status === "Pending");
  const approvedUnpaid = claims.filter((c) => c.status === "Approved");
  const totalApproved = approvedUnpaid.reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Payroll · Expense Claims" title="Claims">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="submit-claim-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> Submit Claim</Button></DialogTrigger>
          <DialogContent className="bg-popover max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Submit a Claim</DialogTitle><DialogDescription>Upload your receipt and enter the amount. HR will review and pay it in cash or in your next payroll.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="claim-category"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">{catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Amount (S$)</Label><Input type="number" step="0.01" min="0" data-testid="claim-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" placeholder="0.00" /></div>
              </div>
              {isFlexi && (
                <div className={`text-xs rounded-md border p-2 ${overFlexi ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-border bg-muted/40 text-muted-foreground"}`} data-testid="flexi-hint">
                  Flexi Care remaining this year: <b>{fmtMoney(flexi.remaining)}</b> of {fmtMoney(flexi.allowance)}.{flexi.monthly_remaining != null ? ` Monthly limit remaining: ${fmtMoney(flexi.monthly_remaining)}.` : ""}{overFlexi ? (overMonthly ? " This claim exceeds this month's limit." : " This claim exceeds your balance.") : ""}
                </div>
              )}
              <div><Label>Claim Date</Label><Input type="date" data-testid="claim-date" value={form.claim_date} onChange={(e) => setForm({ ...form, claim_date: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Description</Label><Textarea data-testid="claim-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" placeholder="What is this claim for?" /></div>
              <div><Label className="mb-1.5 block">Receipt</Label><UploadButton label={form.receipt_file_id ? "Receipt attached" : "Attach receipt"} done={!!form.receipt_file_id} onUploaded={(f) => setForm({ ...form, receipt_file_id: f.id })} /></div>
            </div>
            <DialogFooter><Button onClick={submit} disabled={!(parseFloat(form.amount) > 0) || overFlexi} data-testid="save-claim">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageTitle>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Clock} label="Pending Review" value={pending.length} accent="text-warning" />
        <StatCard icon={Check} label="Approved (unpaid)" value={approvedUnpaid.length} accent="text-success" />
        <StatCard icon={CircleDollarSign} label="Approved Amount" value={fmtMoney(totalApproved)} />
      </div>

      <SectionCard title={isAdmin ? "All Claims" : "My Claims"}>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              {isAdmin && <TableHead>Employee</TableHead>}
              <TableHead>Category</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Description</TableHead>
              <TableHead>Receipt</TableHead><TableHead>Payout</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {claims.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center text-muted-foreground py-8">No claims yet.</TableCell></TableRow>}
              {claims.map((c) => (
                <TableRow key={c.id} data-testid={`claim-row-${c.id}`}>
                  {isAdmin && <TableCell className="font-medium">{c.employee_name}</TableCell>}
                  <TableCell className="text-sm">{c.category}</TableCell>
                  <TableCell className="text-sm">{fmtDate(c.claim_date)}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(c.amount)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate" title={c.description}>{c.description || "—"}</TableCell>
                  <TableCell>{c.receipt_file_id ? <a href={fileUrl(c.receipt_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center text-primary text-sm" data-testid={`receipt-${c.id}`}><Paperclip className="h-4 w-4 mr-1" /> View</a> : <span className="text-muted-foreground text-sm">None</span>}</TableCell>
                  <TableCell className="text-sm" data-testid={`claim-payout-${c.id}`}>
                    {c.status === "Pending" || c.status === "Rejected" ? <span className="text-muted-foreground">—</span> : (c.payout_method === "cash" ? (c.status === "Paid" ? "Paid in cash" : "Cash") : (c.status === "Paid" ? "Paid via payroll" : "Next payroll"))}
                    {c.cash_voucher_file_id && <a href={fileUrl(c.cash_voucher_file_id)} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center text-primary text-xs" data-testid={`voucher-${c.id}`}><Paperclip className="h-3.5 w-3.5 mr-0.5" /> Voucher</a>}
                  </TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {isAdmin && c.status === "Approved" && c.payout_method === "cash" && (
                        <span data-testid={`pay-cash-${c.id}`}><UploadButton label="Upload voucher & mark paid" onUploaded={(f) => payCash(c.id, f)} /></span>
                      )}
                      {isAdmin && c.status === "Pending" && (
                        <Button size="sm" variant="outline" onClick={() => { setReviewFor(c); setNote(""); setPayout("payroll"); }} data-testid={`review-${c.id}`}>Review</Button>
                      )}
                      {((!isAdmin && c.status === "Pending") || (isAdmin && c.status !== "Paid")) && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(c.id)} data-testid={`delete-claim-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      {/* Review dialog */}
      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="bg-popover max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Review Claim</DialogTitle><DialogDescription>{reviewFor?.employee_name} · {reviewFor?.category} · {reviewFor && fmtMoney(reviewFor.amount)}</DialogDescription></DialogHeader>
          {reviewFor && (
            <div className="space-y-3 py-2">
              {reviewFor.description && <p className="text-sm text-muted-foreground">{reviewFor.description}</p>}
              {reviewFor.receipt_file_id && <a href={fileUrl(reviewFor.receipt_file_id)} target="_blank" rel="noreferrer"><img src={fileUrl(reviewFor.receipt_file_id)} alt="receipt" className="max-h-56 rounded-md border border-border" /></a>}
              <div>
                <Label>How will it be paid?</Label>
                <Select value={payout} onValueChange={setPayout}>
                  <SelectTrigger className="mt-1.5" data-testid="claim-payout-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="payroll">To be paid in next payroll</SelectItem>
                    <SelectItem value="cash">Pay in cash (upload cash voucher to mark paid)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Note (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid="review-note" placeholder="Reason / remarks" /></div>
            </div>
          )}
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" className="text-destructive flex-1" onClick={() => review(reviewFor.id, "reject")} data-testid="reject-claim"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
            <Button className="flex-1" onClick={() => review(reviewFor.id, "approve")} data-testid="approve-claim"><Check className="h-4 w-4 mr-1.5" /> Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
